import React from "react";
import {
  FileText,
  Factory,
  ShieldCheck,
  Ship,
  PackageCheck,
  Box,
  Droplet,
  Container,
  Scale,
  BadgeCheck,
  Search,
  Bell,
} from "lucide-react";
import { StatusBadge } from "./primitives";

const sidebar = [
  { label: "Purchase Order", icon: FileText, active: true },
  { label: "Production", icon: Factory },
  { label: "Inspection", icon: ShieldCheck },
  { label: "Freight Booking", icon: Ship },
  { label: "Shipment", icon: PackageCheck },
];

const Widget = ({ icon: Icon, label, value, sub, badge }) => (
  <div className="flex flex-col gap-2 rounded-lg border border-slate-100 bg-white p-4 shadow-card-base">
    <div className="flex items-center justify-between">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-demax-soft text-demax">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
      </span>
      {badge}
    </div>
    <span className="text-[11px] font-medium uppercase tracking-wider text-slateink">
      {label}
    </span>
    <span className="text-[15px] font-semibold leading-tight text-ink">{value}</span>
    {sub && <span className="text-[11px] text-slate-400">{sub}</span>}
  </div>
);

export const HeroDashboard = () => {
  return (
    <div
      data-testid="hero-dashboard"
      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-floating"
    >
      {/* window chrome */}
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3">
        <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
        <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
        <span className="h-3 w-3 rounded-full bg-[#28C840]" />
        <div className="ml-3 hidden items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1 sm:flex">
          <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" />
          <span className="text-[11px] font-medium text-slateink">
            app.demaxtore.com/workspace/fullcontainer
          </span>
        </div>
      </div>

      <div className="flex">
        {/* sidebar */}
        <aside className="hidden w-[188px] shrink-0 flex-col border-r border-slate-100 bg-white p-3 md:flex">
          <div className="flex items-center gap-2 px-2 py-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-ink text-white">
              <Box className="h-3 w-3" />
            </span>
            <span className="font-display text-[13px] font-bold text-ink">FullContainer™</span>
          </div>
          <div className="mt-2 flex flex-col gap-0.5">
            {sidebar.map((item) => (
              <div
                key={item.label}
                className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[12px] font-medium transition-colors ${
                  item.active
                    ? "bg-demax-soft text-demax"
                    : "text-slateink hover:bg-slate-50"
                }`}
              >
                <item.icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                {item.label}
              </div>
            ))}
          </div>
          <div className="mt-auto rounded-lg border border-slate-100 bg-slate-50/60 p-3">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-ink">
              <BadgeCheck className="h-3.5 w-3.5 text-[#059669]" />
              Verified Supplier
            </div>
            <p className="mt-1 text-[10px] leading-relaxed text-slateink">
              Ege Gıda A.Ş. · İzmir, Türkiye
            </p>
          </div>
        </aside>

        {/* main */}
        <div className="flex-1 p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium text-slateink">Order</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slateink">
                  #FC-20481
                </span>
              </div>
              <h3 className="mt-0.5 font-display text-[16px] font-bold text-ink">
                Full Container · Single Supplier
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slateink sm:flex">
                <Search className="h-3.5 w-3.5" />
              </span>
              <span className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slateink">
                <Bell className="h-3.5 w-3.5" />
              </span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
            <Widget icon={Droplet} label="Commodity" value="Sunflower Oil" sub="Refined · Food grade" />
            <Widget icon={Container} label="Container Type" value="40' HC" sub="High cube" />
            <Widget icon={Scale} label="Quantity" value="24 MT" sub="Full container load" />
            <Widget
              icon={Factory}
              label="Supplier"
              value="Verified Manufacturer"
              sub="Turkish factory · direct"
              badge={<StatusBadge tone="success" dot>Verified</StatusBadge>}
            />
            <Widget
              icon={FileText}
              label="Order Status"
              value="Confirmed"
              badge={<StatusBadge tone="success">PO Signed</StatusBadge>}
            />
            <Widget
              icon={Ship}
              label="Shipment Status"
              value="Preparing"
              badge={<StatusBadge tone="warning">In progress</StatusBadge>}
            />
          </div>

          {/* progress rail */}
          <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50/50 p-4">
            <div className="flex items-center justify-between text-[11px] font-medium text-slateink">
              <span>Operation progress</span>
              <span className="text-ink">42%</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
              <div className="h-full w-[42%] rounded-full bg-demax" />
            </div>
            <div className="mt-3 flex items-center justify-between text-[10px] font-medium text-slate-400">
              <span className="text-demax">PO</span>
              <span className="text-demax">Production</span>
              <span>Inspection</span>
              <span>Freight</span>
              <span>Shipment</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
